/// <reference types="vite/client" />

/**
 * PostHog Analytics Configuration and Initialization
 * Optimized for LearnYourself React/TypeScript application
 */

import posthog from 'posthog-js';
import type { AnalyticsConfig } from './analyticsTypes';

// Analytics configuration with environment-based settings
const getAnalyticsConfig = (): AnalyticsConfig => ({
  enabled: process.env.NODE_ENV === 'production' || process.env.VITE_ENABLE_ANALYTICS === 'true',
  debug: process.env.NODE_ENV === 'development',
  batch_size: 10,
  batch_timeout_ms: 2000,
  track_internal_users: false,
  sample_rate: 1.0 // Track 100% of events
});

// Internal user detection
const isInternalUser = (email?: string): boolean => {
  if (!email) return false;
  
  const internalDomains = ['learnyourself.com', 'learnyourself.dev'];
  return internalDomains.some(domain => email.includes(domain));
};

// Device and browser detection
const getDeviceInfo = () => ({
  user_agent_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent),
  browser: navigator.userAgent.split(' ').pop() || 'unknown',
  screen_resolution: `${screen.width}x${screen.height}`,
  viewport_size: `${window.innerWidth}x${window.innerHeight}`
});

// Initialize PostHog with optimized configuration
export const initializePostHog = (): void => {
  if (typeof window === 'undefined') return;

  const config = getAnalyticsConfig();
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (!apiKey) {
    console.warn('PostHog API key not found. Analytics disabled.');
    return;
  }

  // Prevent double initialization
  if (posthog.__loaded) {
    console.log('PostHog already initialized');
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: apiHost,
      
      // Performance optimizations for 2025
      defaults: '2025-05-24', // Use latest defaults for better SPA support
      capture_pageview: false, // We handle pageviews manually via PageTracker
      capture_pageleave: true,  // Enable automatic page leave tracking
      
      // Debug mode for development
      debug: process.env.NODE_ENV === 'development',
      
      // Privacy & compliance settings
      person_profiles: 'identified_only',
      respect_dnt: true,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: { 
          password: true,
          email: true
        }
      },
      
      // Enable default autocapture - tracks all buttons, links, and forms automatically
      autocapture: true,
      
      // Storage and persistence
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      secure_cookie: import.meta.env.PROD,
      cookie_expiration: 365, // 1 year
      
      // Advanced settings
      property_blacklist: [
        '$performance_raw',
        '$autocapture_disabled_server_side'
      ],
      
      // Batch settings for performance
      // Note: batch_size and batch_timeout_ms might not be in PostHogConfig types
      // but they are supported by the library
      
      // Feature flags and experiments
      bootstrap: {
        featureFlags: {
          'detailed-analytics': true,
          'conversion-tracking': true
        }
      },
      
      // Development settings (removed duplicate debug - already set above)
      
      // Initialization callback
      loaded: (posthog) => {
        console.log('🔥 PostHog initialized successfully');
        
        // Expose to window for debugging in development
        if (process.env.NODE_ENV === 'development' || import.meta.env.DEV) {
          (window as any).posthog = posthog;
          console.log('🔍 PostHog exposed to window.posthog for debugging');
          console.log('📝 Run posthog.debug() in console to see captured events');
        }
        
        // Development environment handling
        if (import.meta.env.DEV && !config.enabled) {
          posthog.opt_out_capturing();
          console.log('📊 PostHog tracking disabled in development');
          return;
        }
        
        // Internal user filtering
        const currentUser = getCurrentUser();
        if (currentUser?.email && isInternalUser(currentUser.email)) {
          if (!config.track_internal_users) {
            posthog.opt_out_capturing();
            console.log('🚫 PostHog tracking disabled for internal user');
            return;
          } else {
            posthog.identify(currentUser.id, { 
              ...currentUser,
              internal_user: true 
            });
          }
        }
        
        // Set global properties
        posthog.register({
          app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
          environment: import.meta.env.VITE_ENV || import.meta.env.NODE_ENV,
          build_timestamp: import.meta.env.VITE_BUILD_TIMESTAMP,
          ...getDeviceInfo()
        });
        
        // Track initialization
        posthog.capture('analytics_initialized', {
          config_enabled: config.enabled,
          environment: import.meta.env.NODE_ENV,
          timestamp: new Date().toISOString()
        });
      },
      
      // Error handling
      on_xhr_error: (failedRequest: any) => {
        console.error('PostHog XHR failed:', failedRequest);
        // XHR properties may vary, check what's available
        if (failedRequest) {
          console.error('Failed status:', failedRequest.status);
          console.error('Failed response:', failedRequest.responseText);
        }
      }
    });

  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
  }
};

// Helper to get current user from your auth context/localStorage
const getCurrentUser = () => {
  try {
    // Try to get user from localStorage or your auth store
    const userStr = localStorage.getItem('user') || localStorage.getItem('auth_user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    
    // Fallback: check if there's a global user object
    if ((window as any).currentUser) {
      return (window as any).currentUser;
    }
    
    return null;
  } catch (error) {
    console.warn('Could not retrieve current user:', error);
    return null;
  }
};

// Utility function to safely capture events with fallbacks
export const safeCapture = (eventName: string, properties?: Record<string, any>): void => {
  try {
    if (typeof window === 'undefined' || !posthog.__loaded) {
      if (import.meta.env.DEV) {
        console.warn(`PostHog not available. Would have tracked: ${eventName}`, properties);
      }
      return;
    }
    
    // Add common properties to all events
    const enhancedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_url: window.location.href,
      referrer: document.referrer,
      ...getDeviceInfo()
    };
    
    posthog.capture(eventName, enhancedProperties);
  } catch (error) {
    console.error(`Error capturing event ${eventName}:`, error);
  }
};

// Identify user with enhanced properties
export const identifyUser = (userId: string, userProperties?: Record<string, any>): void => {
  try {
    if (typeof window === 'undefined' || !posthog.__loaded) {
      console.warn('PostHog not available for user identification');
      return;
    }
    
    const enhancedProperties = {
      ...userProperties,
      first_seen: new Date().toISOString(),
      ...getDeviceInfo()
    };
    
    posthog.identify(userId, enhancedProperties);
  } catch (error) {
    console.error('Error identifying user:', error);
  }
};

// Reset PostHog (useful for logout)
export const resetAnalytics = (): void => {
  try {
    if (typeof window !== 'undefined' && posthog.__loaded) {
      posthog.reset();
      console.log('PostHog reset successfully');
    }
  } catch (error) {
    console.error('Error resetting PostHog:', error);
  }
};

// Check if PostHog is loaded and ready
export const isPostHogLoaded = (): boolean => {
  // Check the imported posthog instance directly, not window.posthog
  // The __loaded property is set on the imported instance after init()
  return typeof window !== 'undefined' && posthog.__loaded === true;
};

// Get PostHog instance
export const getPostHog = () => {
  if (typeof window !== 'undefined') {
    // Return the imported posthog instance
    return posthog;
  }
  return null;
};

// Feature flag helper
export const isFeatureEnabled = (flagName: string, defaultValue: boolean = false): boolean => {
  try {
    const posthog = getPostHog();
    if (posthog) {
      return posthog.isFeatureEnabled(flagName) ?? defaultValue;
    }
    return defaultValue;
  } catch (error) {
    console.warn(`Error checking feature flag ${flagName}:`, error);
    return defaultValue;
  }
};

// Export configuration for use in other modules
export { getAnalyticsConfig };